import { prefix } from '../lib/logger';
import type { TextProvider } from '../provider/text';
import type { TextLayoutProvider } from '../provider/text-layout';
import { TextNode } from '../render/text';
import { getLastTextIndex } from '../render/utils/text';

const { error } = prefix('text-highlight');

export function autoSelection(context, list): readonly [number, number] | null {
  const textProvider = context.providers.text as TextProvider;
  const node = textProvider.currentNode;
  if (!textProvider.inputController?.active || !node) return null;
  const selection = textProvider.getCurrentSelection();
  const selectionText = textProvider.getCurrentSelectionText();

  if (selection[0] === selection[1]) {
    if (selection[0] === 0) return null;
    const matchingItem = list.find(
      (item) => item.selection[0] <= selection[0] && item.selection[1] >= selection[0]
    );
    if (matchingItem) {
      return [matchingItem.selection[0], matchingItem.selection[1]];
    }
    return null;
  }
  if (!selectionText) return null;
  return selection;
}

export function addHighlightAction(
  context,
  highlight,
  { selection, node }: { selection?: readonly [number, number]; node?: TextNode } = {}
) {
  const textProvider = context.providers.text as TextProvider;
  const textLayoutProvider = context.providers.textLayout as TextLayoutProvider;
  node = node ?? textProvider.currentNode ?? undefined;
  if (!node) {
    error('No node provided or current node available');
    return;
  }

  const textLayout = textLayoutProvider?.getTextLayout(node.id);
  const allSelection: readonly [number, number] = [0, getLastTextIndex(textLayout)];
  const highlights = [...(node.highlights || [])];

  selection = selection ? selection : autoSelection(context, highlights) || allSelection;

  if (!selection || !Array.isArray(selection) || selection.length !== 2) {
    error('Invalid selection: must be an array of two numbers [start, end]');
    return;
  }

  if (selection[0] < 0 || selection[1] < 0) {
    error('Invalid selection: start and end positions cannot be negative');
    return;
  }
  if (selection[0] > selection[1]) {
    error('Invalid selection: start position must be less than end position');
    return;
  }
  if (selection[1] > allSelection[1]) {
    error('Invalid selection: end position exceeds text length');
    return;
  }

  // Do nothing for zero-length selections
  if (selection[0] === selection[1]) {
    return;
  }

  // Create new highlight with validated selection
  const newHighlight = {
    ...highlight,
    selection: [...selection],
  };
  const [newStart, newEnd] = newHighlight.selection;

  // Apply split and cover strategy.
  // Existing highlights are split or removed where they overlap with the new highlight.
  let nextHighlights = highlights.flatMap((h) => {
    if (!h.selection || h.selection.length < 2) {
      return []; // remove invalid highlights
    }
    const [start, end] = h.selection;

    // No overlap, keep the existing highlight
    if (end <= newStart || start >= newEnd) {
      return h;
    }

    const parts: {
      selection: [number, number];
    }[] = [];
    // Keep the part of the existing highlight that is before the new one
    if (start < newStart) {
      parts.push({ ...h, selection: [start, newStart] });
    }
    // Keep the part of the existing highlight that is after the new one
    if (end > newEnd) {
      parts.push({ ...h, selection: [newEnd, end] });
    }
    return parts;
  });

  // Add the new highlight
  nextHighlights.push(newHighlight);

  // Filter out any zero-length highlights that might have been created during split
  nextHighlights = nextHighlights.filter((h) => h.selection && h.selection[0] < h.selection[1]);

  // Sort highlights by start position for consistent processing
  nextHighlights.sort((a, b) => a.selection[0] - b.selection[0]);

  // Helper to compare highlight styles for merging
  const areStylesEqual = (h1, h2) => {
    // A simple implementation for comparing style properties, excluding selection and id.
    const style1 = { ...h1 };
    const style2 = { ...h2 };
    delete style1.selection;
    delete style1.id;
    delete style2.selection;
    delete style2.id;

    const keys1 = Object.keys(style1);
    const keys2 = Object.keys(style2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (style1[key] !== style2[key]) {
        return false;
      }
    }
    return true;
  };

  // Merge adjacent or overlapping highlights that have the same style
  const mergedHighlights = nextHighlights
    .reduce(
      (acc, current) => {
        const last = acc.length > 0 ? acc[acc.length - 1] : null;

        if (last && areStylesEqual(last, current) && last.selection[1] >= current.selection[0]) {
          // Merge: extend the last highlight's selection
          last.selection[1] = Math.max(last.selection[1], current.selection[1]);
        } else {
          acc.push(current);
        }
        return acc;
      },
      [] as typeof nextHighlights
    )
    .map((item) => {
      return {
        ...item,
        selection: [...item.selection],
      };
    });

  context.api.updateNode(node.id, {
    highlights: mergedHighlights,
  });
}

export function cancelHighlightAction(
  context,
  { selection, node }: { selection?: readonly [number, number]; node?: TextNode } = {}
) {
  const textProvider = context.providers.text;
  const textLayoutProvider = context.providers.textLayout as TextLayoutProvider;
  node = node || textProvider.currentNode;

  if (!node) {
    error('No node provided or current node available');
    return;
  }

  if (!node.highlights) return;
  const list = node.highlights || [];
  const textLayout = textLayoutProvider?.getTextLayout(node.id);
  const allSelection: readonly [number, number] = [0, getLastTextIndex(textLayout)];
  selection = selection || autoSelection(context, list) || allSelection;

  const highlights = list
    .flatMap((highlight) => {
      // Case 1: Highlight is completely within selection - remove it
      if (highlight.selection[0] >= selection[0] && highlight.selection[1] <= selection[1]) {
        return null;
      }

      // Case 2: Selection is completely within highlight - split into two parts
      if (highlight.selection[0] < selection[0] && highlight.selection[1] > selection[1]) {
        return [
          {
            ...highlight,
            selection: [highlight.selection[0], selection[0]],
          },
          {
            ...highlight,
            selection: [selection[1], highlight.selection[1]],
          },
        ];
      }

      // Case 3: Highlight overlaps with selection on the left side
      if (highlight.selection[0] < selection[0] && highlight.selection[1] > selection[0]) {
        return {
          ...highlight,
          selection: [highlight.selection[0], selection[0]],
        };
      }

      // Case 4: Highlight overlaps with selection on the right side
      if (highlight.selection[0] < selection[1] && highlight.selection[1] > selection[1]) {
        return {
          ...highlight,
          selection: [selection[1], highlight.selection[1]],
        };
      }

      // Case 5: No overlap - keep the highlight as is
      return highlight;
    })
    .filter((highlight) => highlight && highlight.selection[0] < highlight.selection[1]);

  context.api.updateNode(node.id, {
    highlights: [...highlights],
  });
}

export function addTextStyleAction(
  context,
  style,
  {
    selection,
    node,
  }: {
    selection?: readonly [number, number];
    node?: TextNode;
  } = {}
) {
  const textProvider = context.providers.text as TextProvider;
  const textLayoutProvider = context.providers.textLayout as TextLayoutProvider;
  node = node || textProvider.currentNode || undefined;

  if (!node) {
    error('No node provided or current node available');
    return;
  }

  const result = textLayoutProvider.getTextLayout(node.id);
  if (!result) {
    error('Text layout is not found for node: ' + node.id);
    return;
  }

  const allSelection: readonly [number, number] = [0, getLastTextIndex(result)];
  const styles = [...(node.styles || [])];

  // Determine selection if not explicitly provided
  if (!selection) {
    const isEditing = textProvider.hasEditing;
    const currentSelection = autoSelection(context, styles) || allSelection;
    const start = currentSelection[0];
    const end = currentSelection[1];
    if (isEditing && start !== end) {
      selection = [start, end];
    } else if (isEditing) {
      // Collapsed selection (cursor)
      const cursor = start;
      // Find the style segment the cursor is in.
      // A cursor at a boundary is considered part of the preceding segment.
      const styleAtCursor = styles
        .slice()
        .reverse()
        .find((s) => cursor >= s.selection[0] && cursor <= s.selection[1]);

      if (styleAtCursor) {
        selection = styleAtCursor.selection;
      } else {
        // When cursor is not within any style, apply to the node's base properties.
        const updateData: any = {};
        if (style.fill !== undefined) updateData.fill = style.fill;
        if (style.fontWeight !== undefined) updateData.fontWeight = style.fontWeight;

        if (Object.keys(updateData).length > 0) {
          context.api.updateNode(node.id, updateData);
        }
        return;
      }
    } else {
      selection = currentSelection || allSelection;
    }
  }

  // Validate selection
  if (
    !selection ||
    !Array.isArray(selection) ||
    selection.length !== 2 ||
    typeof selection[0] !== 'number' ||
    typeof selection[1] !== 'number' ||
    selection[0] > selection[1] ||
    selection[0] < 0
  ) {
    return;
  }

  if (selection[0] === selection[1]) {
    return; // Do nothing for zero-length selections that are not cursors
  }

  // 1. Collect all unique start/end points from existing styles and the new selection
  const points = new Set([selection[0], selection[1]]);
  styles.forEach((s) => {
    points.add(s.selection[0]);
    points.add(s.selection[1]);
  });

  const sortedPoints = Array.from(points)
    .sort((a, b) => a - b)
    .filter((p) => p >= 0 && p <= result.text.length);

  // 2. Create new styled segments for each interval between points
  const newSegments: {
    selection: [number, number];
  }[] = [];
  for (let i = 0; i < sortedPoints.length - 1; i++) {
    const start = sortedPoints[i];
    const end = sortedPoints[i + 1];

    if (start >= end) continue;

    const midPoint = start + (end - start) / 2;
    let segmentStyle: any = {};

    // Inherit from existing style
    const existingStyle = styles.find(
      (s) => midPoint >= s.selection[0] && midPoint < s.selection[1]
    );
    if (existingStyle) {
      segmentStyle = { ...existingStyle };
      delete segmentStyle.selection;
    }

    // Apply new style if segment is within the new selection
    if (midPoint >= selection[0] && midPoint < selection[1]) {
      segmentStyle = { ...segmentStyle, ...style };
    }

    // Optimization: remove properties that match the node's base style
    const nodeFontWeight = node.fontWeight || 'normal';
    if (segmentStyle.fontWeight && segmentStyle.fontWeight === nodeFontWeight) {
      delete segmentStyle.fontWeight;
    }
    if (segmentStyle.fill && segmentStyle.fill === node.fill) {
      delete segmentStyle.fill;
    }

    // Only add segment if it has any style properties left
    if (Object.keys(segmentStyle).length > 0) {
      newSegments.push({ ...segmentStyle, selection: [start, end] });
    }
  }

  // 3. Merge adjacent segments that have identical styles
  const finalStyles: {
    selection: [number, number];
  }[] = [];
  if (newSegments.length > 0) {
    let currentSegment = { ...newSegments[0] };
    for (let i = 1; i < newSegments.length; i++) {
      const nextSegment = newSegments[i];
      const currentProps = { ...currentSegment };
      // @ts-expect-error allow this
      delete currentProps.selection;
      const nextProps = { ...nextSegment };
      // @ts-expect-error allow this
      delete nextProps.selection;

      const propsMatch =
        Object.keys(currentProps).length === Object.keys(nextProps).length &&
        Object.keys(currentProps).every((key) => currentProps[key] === nextProps[key]);

      if (currentSegment.selection[1] === nextSegment.selection[0] && propsMatch) {
        currentSegment.selection[1] = nextSegment.selection[1];
      } else {
        finalStyles.push(currentSegment);
        currentSegment = { ...nextSegment };
      }
    }
    finalStyles.push(currentSegment);
  }

  // 4. Finalize and update the node
  if (finalStyles.length === 0) {
    context.api.updateNode(node.id, { styles: null });
    return;
  }

  // If one style covers the whole text, lift its properties to the node level
  if (
    finalStyles.length === 1 &&
    finalStyles[0].selection[0] === 0 &&
    finalStyles[0].selection[1] === result.text.length
  ) {
    const updateData = { styles: null, ...finalStyles[0] };
    // @ts-expect-error allow this
    delete updateData.selection;
    context.api.updateNode(node.id, updateData);
    return;
  }

  context.api.updateNode(node.id, { styles: finalStyles });
}

export function cancelTextStyleAction(
  context,
  {
    selection,
    node,
  }: {
    selection?: readonly [number, number];
    node?: TextNode;
  } = {}
) {
  const textProvider = context.providers.text as TextProvider;
  const textLayoutProvider = context.providers.textLayout as TextLayoutProvider;
  node = node || textProvider.currentNode || undefined;
  if (!node) {
    error('No node provided or current node available');
    return;
  }

  const styles = [...(node.styles || [])];
  if (styles.length === 0) return;

  const result = textLayoutProvider.getTextLayout(node.id);
  if (!result) {
    error('Text layout is not found for node: ' + node.id);
    return;
  }
  const allSelection: readonly [number, number] = [0, getLastTextIndex(result)];

  // Determine selection if not explicitly provided
  if (!selection) {
    const isEditing = textProvider.hasEditing;
    const currentSelection = autoSelection(context, styles) || [0, 0];
    const start = currentSelection[0];
    const end = currentSelection[1];
    if (isEditing && start !== end) {
      selection = [start, end];
    } else if (isEditing) {
      // Collapsed selection (cursor)
      const cursor = start;
      // Find the style segment the cursor is in.
      const styleAtCursor = styles
        .slice()
        .reverse()
        .find((s) => cursor >= s.selection[0] && cursor <= s.selection[1]);

      if (styleAtCursor) {
        selection = styleAtCursor.selection;
      } else {
        // Cursor is not within any styled text, so there's nothing to cancel.
        return;
      }
    } else {
      selection = currentSelection || allSelection;
    }
  }

  // Validate selection
  if (
    !selection ||
    !Array.isArray(selection) ||
    selection.length !== 2 ||
    typeof selection[0] !== 'number' ||
    typeof selection[1] !== 'number' ||
    selection[0] > selection[1] ||
    selection[0] < 0
  ) {
    return;
  }

  if (selection[0] === selection[1]) {
    return; // Do nothing for zero-length selections that are not cursors
  }

  // 1. Collect all unique start/end points from existing styles and the selection
  const points = new Set([selection[0], selection[1]]);
  styles.forEach((s) => {
    points.add(s.selection[0]);
    points.add(s.selection[1]);
  });

  const sortedPoints = Array.from(points)
    .sort((a, b) => a - b)
    .filter((p) => p >= 0 && p <= result.text.length);

  // 2. Create new styled segments for each interval between points
  const newSegments: {
    selection: [number, number];
  }[] = [];
  for (let i = 0; i < sortedPoints.length - 1; i++) {
    const start = sortedPoints[i];
    const end = sortedPoints[i + 1];

    if (start >= end) continue;

    const midPoint = start + (end - start) / 2;
    let segmentStyle: any = {};

    // Inherit from existing style
    const existingStyle = styles.find(
      (s) => midPoint >= s.selection[0] && midPoint < s.selection[1]
    );
    if (existingStyle) {
      segmentStyle = { ...existingStyle };
      delete segmentStyle.selection;
    }

    // If segment is within the cancellation selection, clear its styles
    if (midPoint >= selection[0] && midPoint < selection[1]) {
      segmentStyle = {};
    }

    // Only add segment if it has any style properties left
    if (Object.keys(segmentStyle).length > 0) {
      newSegments.push({ ...segmentStyle, selection: [start, end] });
    }
  }

  // 3. Merge adjacent segments that have identical styles
  const finalStyles: {
    selection: [number, number];
  }[] = [];
  if (newSegments.length > 0) {
    let currentSegment = { ...newSegments[0] };
    for (let i = 1; i < newSegments.length; i++) {
      const nextSegment = newSegments[i];
      const currentProps = { ...currentSegment };
      // @ts-expect-error allow this
      delete currentProps.selection;
      const nextProps = { ...nextSegment };
      // @ts-expect-error allow this
      delete nextProps.selection;

      const propsMatch =
        Object.keys(currentProps).length === Object.keys(nextProps).length &&
        Object.keys(currentProps).every((key) => currentProps[key] === nextProps[key]);

      if (currentSegment.selection[1] === nextSegment.selection[0] && propsMatch) {
        currentSegment.selection[1] = nextSegment.selection[1];
      } else {
        finalStyles.push(currentSegment);
        currentSegment = { ...nextSegment };
      }
    }
    finalStyles.push(currentSegment);
  }

  // 4. Finalize and update the node
  if (finalStyles.length === 0) {
    context.api.updateNode(node.id, { styles: null });
    return;
  }

  // If one style covers the whole text, lift its properties to the node level
  if (
    finalStyles.length === 1 &&
    finalStyles[0].selection[0] === 0 &&
    finalStyles[0].selection[1] === result.text.length
  ) {
    const updateData = { styles: null, ...finalStyles[0] };
    // @ts-expect-error allow this
    delete updateData.selection;
    context.api.updateNode(node.id, updateData);
    return;
  }

  context.api.updateNode(node.id, { styles: finalStyles });
}
